import { useMemo } from 'react';
import { ChakraFlex, ChakraVStack, FAIconName, Icon, Text } from '@equip.health/ui';
import AppointmentCard from '~/components/common/calendar/AppointmentCard';
import { APPOINTMENT_LIST_TEST_ID } from '../constants';
import { formatFullWeekdayDate, formatAppointmentShortTime } from '../utils';

export interface ConfirmationAppointment {
  startTime: string;
  endTime: string;
  appointmentType: string;
  providerId: string;
  isIntake: boolean;
}

export interface AppointmentListProps {
  appointments: ConfirmationAppointment[];
  patientTimezone?: string;
}

const AppointmentList = ({
  appointments,
  patientTimezone,
}: AppointmentListProps) => {
  const formatDate = (isoString: string) =>
    formatFullWeekdayDate(isoString, patientTimezone);

  const formatStartEndTime = (startIso: string, endIso: string) =>
    formatAppointmentShortTime(startIso, endIso, patientTimezone);

  // Sorted chronologically; intake always precedes its follow-ups, so this puts it first.
  const sortedAppointments = useMemo(
    () =>
      [...appointments].sort(
        (a, b) =>
          new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
      ),
    [appointments],
  );

  const hasAnyAppointments = sortedAppointments.length > 0;

  if (!hasAnyAppointments) {
    return (
      <ChakraFlex
        data-testid={APPOINTMENT_LIST_TEST_ID}
        flexDirection="column"
        gap="18px"
      >
        <ChakraFlex
          alignItems="center"
          color="neutral.secondary"
          gap="8px"
          padding="18px"
        >
          <Icon name={FAIconName.calendarRegular} />
          <Text variant="body">No appointments scheduled</Text>
        </ChakraFlex>
      </ChakraFlex>
    );
  }

  return (
    <ChakraFlex
      data-testid={APPOINTMENT_LIST_TEST_ID}
      flexDirection="column"
      gap="18px"
    >
      <ChakraVStack alignItems="stretch" spacing="12px">
        {sortedAppointments.map((appointment) => (
          <AppointmentCard
            endTimeISO={appointment.endTime}
            formatDate={formatDate}
            formatStartEndTime={formatStartEndTime}
            id={`appointment-${appointment.startTime}-${appointment.providerId}`}
            key={`${appointment.startTime}-${appointment.providerId}`}
            startTimeISO={appointment.startTime}
            title={
              appointment.isIntake
                ? appointment.appointmentType
                : appointment.appointmentType || 'Follow-up Session'
            }
          />
        ))}
      </ChakraVStack>
    </ChakraFlex>
  );
};

export default AppointmentList;
